import { getCategoryPageServerSideProps } from 'server/utils/getCategoryPageServerSideProps';
import { IProductLandingPage } from 'shared/types/ProductLandingPage';
import { isSearchCrawler } from 'shared/utils/isSearchCrawler';
import { PlpPageComponent as PLPLandingPageComponent } from 'product-landing-pages/components/PlpPageComponent';
import { setServerSideTranslations } from 'localization/utils/setServerSideTranslations';
import { startCase } from 'shared/utils/startCase';
import { CONSUMER_SEARCH_TAXONOMY_REDIRECTS } from 'shared/utils/consumerSearchTaxononyRedirects';
import { slugify } from 'shared/utils/consumerSearchSlugify';
import {
  FOOTWEAR_ACTIVITIES,
  TAXONOMY_LEVEL_1,
  TAXONOMY_LEVEL_2,
} from 'product-landing-pages/utils/taxonomyLevels';
import { ACTIVIITY_LIST } from 'product-landing-pages/utils/activityList';

const CONSUMER_SEARCH_VALUES = {
  boots: 'boot',
  cleats: 'cleat',
  sandals: 'sandal',
};

export const CONSUMER_SEARCH_VALUES_REVERSE = {
  boot: 'boots',
  cleat: 'cleats',
  sandal: 'sandals',
};

const AccessoriesSubcategory = ({
  breadcrumbs,
  browseKey,
  browseValue,
  category,
  constructorResponse,
  displayName,
  globalPricingId,
  isCrawler = false,
  consumerSearchFilters = {},
  consumerSearchInitialData = '{}',
  pageBreadcrumbs,
}: IProductLandingPage) => {
  const {
    name = displayName,
    description = '',
    metadataDescription = '',
    titleTag = '',
  } = breadcrumbs || {};

  return (
    <PLPLandingPageComponent
      breadcrumbs={
        pageBreadcrumbs || [
          { name: startCase(category), slug: `/${category}` },
          {
            name: startCase(browseValue),
            slug: `/${category}/${slugify(browseValue)}`,
            isStatic: true,
          },
        ]
      }
      hasBreadcrumbSchema
      browseKey={browseKey}
      browseValue={browseValue}
      constructorResponse={constructorResponse}
      description={description}
      displayName={name}
      globalPricingId={globalPricingId}
      metadataDescription={metadataDescription}
      metaTitle={titleTag}
      isCrawler={isCrawler}
      consumerSearchFilters={consumerSearchFilters}
      consumerSearchInitialData={JSON.parse(consumerSearchInitialData)}
    />
  );
};

export async function getServerSideProps({ query, req, res, resolvedUrl, locale }) {
  const { pageSlug, taxonomies }: { taxonomies: string[]; pageSlug: string } = query;

  const [subCategory, taxonomyLevel3, taxonomyLevel4] = Array.isArray(taxonomies)
    ? taxonomies
    : [taxonomies];

  const isCrawler = isSearchCrawler(req?.headers);
  const i18n = await setServerSideTranslations({ locale });

  const subCategoryRedirect = CONSUMER_SEARCH_TAXONOMY_REDIRECTS[subCategory];

  if (
    subCategoryRedirect &&
    (pageSlug.toLowerCase() !== subCategoryRedirect.base ||
      subCategory !== subCategoryRedirect.subCategory)
  ) {
    if (!subCategoryRedirect.subCategory) {
      return {
        redirect: {
          destination: `/${subCategoryRedirect.base}`,
          permanent: true,
        },
      };
    }
    return {
      redirect: {
        destination: `/${subCategoryRedirect.base}/${subCategoryRedirect.subCategory}${taxonomyLevel3 ? `/${taxonomyLevel3}` : ''}${taxonomyLevel4 ? `/${taxonomyLevel4}` : ''}`,
        permanent: true,
      },
    };
  }

  let consumerSearchFilters: any = {};

  if (
    TAXONOMY_LEVEL_1.includes(pageSlug.toLowerCase()) ||
    (pageSlug.toLowerCase() === 'sneakers' &&
      TAXONOMY_LEVEL_2.includes(slugify(subCategory.toLowerCase())))
  ) {
    const isL1 = TAXONOMY_LEVEL_1.includes(pageSlug.toLowerCase());
    consumerSearchFilters = {
      categories: !isL1 ? 'footwear' : pageSlug,
      productTypes:
        pageSlug?.toLowerCase() === 'sneakers'
          ? slugify(subCategory)
          : subCategory?.replaceAll('_', '-') || '',
    };
  } else if (TAXONOMY_LEVEL_2.includes(pageSlug.toLowerCase())) {
    if (ACTIVIITY_LIST.includes(subCategory.toLowerCase())) {
      consumerSearchFilters = {
        productTypes: pageSlug?.replaceAll('_', '-') || '',
        activityList: CONSUMER_SEARCH_VALUES[subCategory.toLowerCase()] || subCategory,
      };
    } else {
      consumerSearchFilters = {
        productTypes: pageSlug.replaceAll('_', '-'),
        activities: subCategory,
      };
    }
  }

  if (taxonomyLevel3) {
    consumerSearchFilters.taxonomyLevel3 = slugify(taxonomyLevel3);
  }

  if (taxonomyLevel4) {
    consumerSearchFilters.taxonomyLevel4 = slugify(taxonomyLevel4);
  }

  const displayName = taxonomyLevel4
    ? `${startCase(taxonomyLevel4)}`
    : taxonomyLevel3
      ? `${startCase(taxonomyLevel3)}`
      : subCategory
        ? startCase(subCategory)
        : startCase(pageSlug);

  // for activity link make sure we are passing the right value to consumer search
  // LOOKS LIKE WE CAN HAVE SNEAKERS/BRAND/NIKE/BOOT

  if (
    consumerSearchFilters.categories === 'footwear' &&
    consumerSearchFilters.productTypes &&
    FOOTWEAR_ACTIVITIES.includes(consumerSearchFilters.productTypes?.toLowerCase() as string)
  ) {
    if (
      ['running', 'basketball', 'skateboarding'].includes(
        consumerSearchFilters.productTypes.toLowerCase() as string,
      )
    ) {
      consumerSearchFilters = {
        ...consumerSearchFilters,
        activityList: consumerSearchFilters.productTypes,
      };
      delete consumerSearchFilters.productTypes;
    } else {
      return {
        notFound: true,
      };
    }
  }

  const categoryProps = await getCategoryPageServerSideProps({
    category: pageSlug,
    displayName,
    i18n,
    isCrawler,
    query,
    req,
    res,
    resolvedUrl,
    subCategory: resolvedUrl?.split('/')?.[2],
    taxonomyLevel3,
    taxonomyLevel4,
    hasCSFilters: consumerSearchFilters,
    breadcrumbsCMSPath: `${pageSlug}/${subCategory}${taxonomyLevel3 ? `/${taxonomyLevel3}` : ''}${taxonomyLevel4 ? `/${taxonomyLevel4}` : ''}`,
  });

  return categoryProps;
}

export default AccessoriesSubcategory;
